import { useState, useEffect, useCallback } from "react";
import { debounce } from "./helper";
import { extractTextFromElement } from "./helper";
import useInViewport from "@hooks/useInViewport";

const pushToDataLayer = (event, eventAction, eventLabel, additionalData = {}) => {
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, eventAction, eventLabel, ...additionalData });
  }
};

const GTM = {
  pushToDataLayer,

  init: () => {
    GTM.scrollTracking();
    window.addEventListener("scroll", GTM.scrollTracking);
  },

  /*** Scroll tracking START ***/

  distanceScrolled: -1,
  scrollThresholds: [
    { percentage: 25, triggered: false },
    { percentage: 50, triggered: false },
    { percentage: 75, triggered: false },
    { percentage: 90, triggered: false },
    { percentage: 100, triggered: false },
  ],

  scrollTracking: debounce(() => {
    const scrollPosition = window.scrollY;
    const scrollPercentage = Math.round(((scrollPosition / (document.documentElement.scrollHeight - window.innerHeight)) * 100));

    GTM.distanceScrolled = scrollPosition;

    GTM.scrollThresholds.forEach((threshold) => {
      if (scrollPercentage >= threshold.percentage && !threshold.triggered) {
        threshold.triggered = true;
        pushToDataLayer("custom_scroll_depth", `${threshold.percentage}%`);
      }
    });
  }, 100),

  /*** Scroll tracking END ***/

  /*** Form tags START ***/

  formSubmission: (formName, completedFields) => {
    pushToDataLayer("form_submissions", formName);
    if (completedFields) {
      Object.keys(completedFields).forEach((field) => {
        const label = completedFields[field] ? `${field}_${completedFields[field]}` : field;
        pushToDataLayer("field_completion_submitted", formName, label);
      });
    }
  },

  formFieldCompletion: (formName, fieldName, fieldValue) => {
    const label = fieldValue ? `${fieldName}_${fieldValue}` : fieldName;
    pushToDataLayer("field_completion", formName, label);
  },

  formInputError: (formName, fieldName, fieldError) => {
    pushToDataLayer("validation_errors", formName, `${fieldName}_${fieldError}`);
  },

  getFormFieldName: (field) => {
    switch (field.tagName) {
      case 'INPUT':
        switch (field.type) {
          case 'checkbox':
            return field.getAttribute('value') ? `${field.name}_${field.getAttribute('value')}` : field.name;
          default:
            return field.name;
        }
      default:
        return field.name;
    }
  },

  // Format the value of the field pushed to the data layer based on the type of the field
  // Values typed into text fields should not be sent to the data layer, so return an empty string
  getFormFieldValue: (field) => {
    switch (field.tagName) {
      case 'SELECT':
        return field.value;
      case 'INPUT':
        switch (field.type) {
          case 'checkbox':
            return field.checked ? 'yes' : 'no';
          case 'radio':
            return field.checked ? field.value : '';
          case 'text':
          case 'email':
          case 'password':
          case 'tel':
          case 'number':
          default:
            return '';
        }
      default:
        return '';
    }
  },

  useFormTracking: (formName, formRef = null) => {
    const [completedFields, setCompletedFields] = useState({});

    const handleFieldCompletionTracking = useCallback((event, value) => {
      const fieldName = GTM.getFormFieldName(event.target);
      const fieldValue = value ? value : GTM.getFormFieldValue(event.target);
      setCompletedFields((prev) => ({
        ...prev,
        [fieldName]: fieldValue
      }));
      GTM.formFieldCompletion(formName, fieldName, fieldValue);
    }, [formName]);

    const handleFormSubmissionTracking = () => {
      GTM.formSubmission(formName, completedFields);
    };

    const handleFormInputErrorTracking = (fieldName, fieldError) => {
      GTM.formInputError(formName, fieldName, fieldError);
    };

    useEffect(() => {
      if (formRef?.current) {
        const currentForm = formRef.current;
        currentForm.addEventListener('change', handleFieldCompletionTracking);
        return () => {
          currentForm.removeEventListener('change', handleFieldCompletionTracking);
        };
      }
    }, [formRef, handleFieldCompletionTracking]);

    return {
      handleFieldCompletionTracking,
      handleFormSubmissionTracking,
      handleFormInputErrorTracking,
      completedFields,
      setCompletedFields
    };
  },

  /*** Form tags END ***/

  /*** Video tracking START ***/

  videoTracking: {},

  videoStart: (videoTitle) => {
    pushToDataLayer("video_plays", videoTitle, "Start");
  },

  videoEnd: (videoTitle) => {
    pushToDataLayer("video_plays", videoTitle, "End");
  },

  videoProgress: (playedSeconds, videoTitle, videoDuration) => {
    const currentTime = Math.floor(playedSeconds);
    const percentage = Math.floor(((currentTime + 1) / videoDuration) * 100);

    // Initialize tracking sets for this video if they don't exist
    if (!GTM.videoTracking[videoTitle]) {
      GTM.videoTracking[videoTitle] = {
        loggedTimes: new Set(),
        loggedPercentages: new Set()
      };
    }

    // Log time intervals (every 10 seconds) only once per video
    if (
      currentTime % 10 === 0 &&
      currentTime > 0 &&
      !GTM.videoTracking[videoTitle].loggedTimes.has(currentTime)
    ) {
      GTM.videoTracking[videoTitle].loggedTimes.add(currentTime);
      pushToDataLayer("video_interval_seconds", videoTitle, `${currentTime.toString()}s`);
    }

    // Log percentage in increments of 25 per video
    const roundedPercentage = Math.floor(percentage / 25) * 25;

    if (!GTM.videoTracking[videoTitle].loggedPercentages.has(roundedPercentage)) {
      GTM.videoTracking[videoTitle].loggedPercentages.add(roundedPercentage);
      if (roundedPercentage === 0) return;

      pushToDataLayer("video_percent", videoTitle, `${roundedPercentage.toString()}%`);
    }
  },

  /*** Video tracking END ***/

  expandableContent: (isExpanded, title = "", handlesAll = false) => {
    const action = isExpanded ? "Expand" : "Collapse";
    const expandedString = handlesAll ? `${action} All` : action;
    const titleString = extractTextFromElement(title);
    pushToDataLayer("expansions", expandedString, titleString);
  },

  interstitial: (interstitialVisible, interstitialName) => {
    const event = interstitialVisible ? "interstitial_load" : "interstitial_dismiss";
    pushToDataLayer(event, null, interstitialName);
  },

  isiInteraction: (isExpanded) => {
    pushToDataLayer("isi", null, isExpanded ? "Expand" : "Collapse");
  },

  /*** Custom hook for long-scroll pages ***/
  useInViewportTracking: (ref, onInView = () => { }, { threshold = 0.25, triggerOnce = true } = {}) => {
    const isInView = useInViewport(ref, onInView, { threshold, triggerOnce });
    if (isInView) {
      pushToDataLayer("custom_page_view");
    }
    return isInView;
  },

  clickAction: (action, label = "") => {
    pushToDataLayer("click_action", action, label);
  },

  reset: () => {
    GTM.videoTracking = {};
    GTM.distanceScrolled = -1;
    GTM.scrollThresholds.forEach((threshold) => {
      threshold.triggered = false;
    });
    window.removeEventListener("scroll", GTM.scrollTracking);
  },

  useGTMTracking: () => {
    useEffect(() => {
      GTM.init();

      return () => {
        GTM.reset();  // Required for accurate scroll depth tracking
      };
    }, []);
  }
};

export default GTM;